import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Save, FileText, Plus, Trash2, Package, Wrench, MoreHorizontal,
  Send, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { GST_RATES, OTHER_CHARGE_TYPES, computeLocalTotals, inr } from "./lib";

export default function InvoiceForm() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const jcId = sp.get("job_card");
  const nav = useNavigate();
  const isEdit = !!id;

  const [nextNumber, setNextNumber] = useState("");
  const [parts, setParts] = useState([]);
  const [tasks, setTasks] = useState([]);      // service task master
  const [loading, setLoading] = useState(isEdit || !!jcId);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [collapsed, setCollapsed] = useState({});

  // Bootstrap
  useEffect(() => {
    api.get("/spares/parts", { params: { limit: 1000 } }).then((r) => setParts(r.data || []));
    api.get("/workshop/task-templates").then((r) => setTasks(r.data || [])).catch(() => setTasks([]));
    if (!isEdit) {
      api.get("/billing/invoices/next-number").then((r) => setNextNumber(r.data.next_number || ""));
    }
  }, [isEdit]);

  // Load draft-from-JC or existing invoice
  const bootstrap = useCallback(async () => {
    if (isEdit) {
      try {
        const r = await api.get(`/billing/invoices/${id}`);
        setForm(shapeFromInvoice(r.data));
        setNextNumber(r.data.number);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load invoice");
        nav("/invoices");
      } finally { setLoading(false); }
    } else if (jcId) {
      try {
        const r = await api.post(`/billing/invoices/from-job-card/${jcId}`);
        setForm(shapeFromDraftPayload(r.data));
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not import job card");
      } finally { setLoading(false); }
    }
  }, [isEdit, id, jcId, nav]);
  useEffect(() => { bootstrap(); }, [bootstrap]);

  const totals = useMemo(() => computeLocalTotals(
    form.items, form.invoice_type, form.is_interstate,
    form.discount_amount, form.discount_pct, form.round_off, form.advance_amount,
  ), [form]);

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setItem = (idx, patch) => setForm((f) => ({
    ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
  }));
  const addItem = (kind) => setForm((f) => ({ ...f, items: [...f.items, freshItem(kind)] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const pickPart = (idx, p) => setItem(idx, {
    kind: "part", part_id: p.id, name: p.name,
    part_number: p.part_number || "", unit: p.unit || "nos",
    rate: p.selling_price || 0, gst_rate: form.invoice_type === "gst" ? (p.gst_rate ?? 18) : 0,
  });

  const validationErrors = useMemo(() => {
    const errs = [];
    if (!form.customer_snapshot?.name) errs.push("Customer name is required");
    if (!form.customer_snapshot?.phone) errs.push("Customer phone is required");
    if (!form.vehicle_snapshot?.reg_number) errs.push("Vehicle registration is required");
    if (!form.items.length) errs.push("Add at least one line item");
    if (form.invoice_type === "gst") {
      form.items.forEach((it, i) => {
        if (!GST_RATES.includes(Number(it.gst_rate))) errs.push(`Line ${i + 1}: GST rate must be 0%, 5% or 18%`);
      });
    }
    return errs;
  }, [form]);

  const save = async (issueImmediately = false) => {
    if (validationErrors.length) {
      toast.error(validationErrors[0]);
      return;
    }
    setSaving(true);
    try {
      const payload = shapeToBody(form);
      const r = isEdit
        ? await api.patch(`/billing/invoices/${id}`, payload)
        : await api.post("/billing/invoices", payload);
      const invId = r.data.id;
      if (issueImmediately) {
        await api.post(`/billing/invoices/${invId}/issue`);
      }
      toast.success(isEdit ? "Invoice updated" : `${r.data.number} ${issueImmediately ? "issued" : "saved"}`);
      nav(`/invoices/${invId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save invoice");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-10 text-center text-[13px] text-[#667085]">Loading invoice…</div>;

  const toggle = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  return (
    <div className="space-y-4" data-testid="invoice-form-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Link to="/invoices" data-testid="invoice-back"
                className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#667085] hover:bg-[#F1FAEE]">
            <ArrowLeft size={15} />
          </Link>
          <div>
            <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">
              {isEdit ? "Edit Invoice" : "New Invoice"}
            </h1>
            <p className="text-[12.5px] text-[#667085] mt-0.5 font-mono">
              {nextNumber} {form.job_card_number ? `· from JC ${form.job_card_number}` : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => save(false)} disabled={saving} data-testid="invoice-save-draft"
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5 disabled:opacity-50">
            <Save size={13} /> Save Draft
          </button>
          <button onClick={() => save(true)} disabled={saving} data-testid="invoice-issue-btn"
                  className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
            <FileText size={13} /> {saving ? "Saving…" : "Save & Issue"}
          </button>
        </div>
      </header>

      {/* Validation errors — surfaced at the TOP so they can't be missed when
          the user tries to save. */}
      {validationErrors.length > 0 && (
        <div className="rounded-lg bg-[#e63946]/10 border-2 border-[#e63946] px-4 py-3 text-[13px] text-[#e63946] font-semibold"
             data-testid="inv-validation-top">
          <div className="flex items-start gap-2">
            <span aria-hidden="true">⚠️</span>
            <div>
              <div>{validationErrors.length === 1 ? "1 issue to fix before saving:" : `${validationErrors.length} issues to fix before saving:`}</div>
              <ul className="mt-1 list-disc list-inside font-normal text-[#e63946]/90">
                {validationErrors.slice(0, 6).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Invoice type + dates */}
          <Card title="Invoice basics" testId="inv-basics">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="inline-flex rounded-md bg-[#F1FAEE] border border-[#E4E7EC] p-0.5 text-[12px]">
                {["gst", "non_gst"].map((t) => (
                  <button key={t} type="button" onClick={() => setF({ invoice_type: t })}
                          data-testid={`inv-type-${t}`}
                          className={`h-8 px-3 rounded font-semibold ${
                            form.invoice_type === t ? "bg-[#1d3557] text-white" : "text-[#457b9d]"
                          }`}>
                    {t === "gst" ? "GST Invoice" : "Non-GST"}
                  </button>
                ))}
              </div>
              {form.invoice_type === "gst" && (
                <label className="inline-flex items-center gap-2 text-[12.5px] text-[#1d3557] ml-2">
                  <input type="checkbox" checked={form.is_interstate}
                         onChange={(e) => setF({ is_interstate: e.target.checked })}
                         data-testid="inv-interstate"
                         className="w-4 h-4 accent-[#e63946]" />
                  Inter-state (IGST)
                </label>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Invoice date">
                <input type="date" value={dateOnly(form.invoice_date)}
                       onChange={(e) => setF({ invoice_date: e.target.value })}
                       data-testid="inv-date"
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
              </Field>
              <Field label="Due date">
                <input type="date" value={dateOnly(form.due_date) || ""}
                       onChange={(e) => setF({ due_date: e.target.value })}
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
              </Field>
              <Field label="Place of supply">
                <input value={form.place_of_supply}
                       onChange={(e) => setF({ place_of_supply: e.target.value })}
                       placeholder="e.g. Karnataka"
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
              </Field>
            </div>
          </Card>

          {/* Customer */}
          <SectionCard title="Customer" open={!collapsed.customer} onToggle={() => toggle("customer")}
                       testId="inv-customer">
            <PartyForm party={form.customer_snapshot} onChange={(cs) => setF({ customer_snapshot: cs })}
                       showGst={form.invoice_type === "gst"} />
          </SectionCard>

          {/* Vehicle */}
          <SectionCard title="Vehicle" open={!collapsed.vehicle} onToggle={() => toggle("vehicle")}
                       testId="inv-vehicle">
            <VehicleForm veh={form.vehicle_snapshot} onChange={(vs) => setF({ vehicle_snapshot: vs })} />
          </SectionCard>

          {/* Line items */}
          <Card title="Line items" testId="inv-items"
                action={
                  <div className="flex gap-1">
                    <button type="button" onClick={() => addItem("part")}
                            data-testid="inv-add-part"
                            className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#457b9d] hover:bg-[#F1FAEE] inline-flex items-center gap-1">
                      <Package size={11} /> Part
                    </button>
                    <button type="button" onClick={() => addItem("labour")}
                            data-testid="inv-add-labour"
                            className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#457b9d] hover:bg-[#F1FAEE] inline-flex items-center gap-1">
                      <Wrench size={11} /> Labour
                    </button>
                    <button type="button" onClick={() => addItem("charge")}
                            data-testid="inv-add-charge"
                            className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#457b9d] hover:bg-[#F1FAEE] inline-flex items-center gap-1">
                      <MoreHorizontal size={11} /> Charge
                    </button>
                  </div>
                }>
            <div className="space-y-2">
              {form.items.length === 0 ? (
                <div className="py-8 text-center text-[12.5px] text-[#667085]">
                  No lines yet. Add a part, labour task or other charge.
                </div>
              ) : (
                form.items.map((it, idx) => (
                  <LineRow key={idx} idx={idx} item={it} parts={parts} tasks={tasks}
                           gstAllowed={form.invoice_type === "gst"}
                           onChange={(patch) => setItem(idx, patch)}
                           onPart={(p) => pickPart(idx, p)}
                           onRemove={() => removeItem(idx)} />
                ))
              )}
            </div>
          </Card>

          {/* Discount + Round-off */}
          <Card title="Discount & round-off" testId="inv-discount">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <Field label="Invoice discount %">
                <input type="number" min={0} max={100} value={form.discount_pct}
                       onChange={(e) => setF({ discount_pct: Number(e.target.value) || 0 })}
                       data-testid="inv-discount-pct"
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
              </Field>
              <Field label="Discount amount">
                <input type="number" min={0} value={form.discount_amount}
                       onChange={(e) => setF({ discount_amount: Number(e.target.value) || 0 })}
                       data-testid="inv-discount-amount"
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
              </Field>
              <Field label="Discount reason">
                <input value={form.discount_reason}
                       onChange={(e) => setF({ discount_reason: e.target.value })}
                       placeholder="Loyalty / promo"
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
              </Field>
              <Field label="Round-off">
                <input type="number" value={form.round_off}
                       onChange={(e) => setF({ round_off: Number(e.target.value) || 0 })}
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
              </Field>
            </div>
          </Card>

          {/* Notes + Terms */}
          <Card title="Notes & terms" testId="inv-notes">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Notes">
                <textarea rows={3} value={form.notes}
                          onChange={(e) => setF({ notes: e.target.value })}
                          className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
              </Field>
              <Field label="Terms & conditions">
                <textarea rows={3} value={form.terms}
                          onChange={(e) => setF({ terms: e.target.value })}
                          className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
              </Field>
            </div>
          </Card>
        </div>

        {/* Sticky summary */}
        <aside>
          <div className="bg-white rounded-xl border border-[#E4E7EC] p-4 sticky top-4" data-testid="inv-summary">
            <h3 className="text-[13px] font-bold text-[#1d3557] mb-3">Summary</h3>
            <SumRow label="Parts" value={inr(totals.parts_subtotal)} muted />
            <SumRow label="Labour" value={inr(totals.labour_subtotal)} muted />
            <SumRow label="Charges" value={inr(totals.charges_subtotal)} muted />
            <div className="my-2 border-t border-[#F1FAEE]" />
            <SumRow label="Gross subtotal" value={inr(totals.gross_subtotal)} />
            {totals.line_discount > 0 && <SumRow label="Line discount" value={`− ${inr(totals.line_discount)}`} muted />}
            {totals.invoice_discount > 0 && <SumRow label="Invoice discount" value={`− ${inr(totals.invoice_discount)}`} muted />}
            <SumRow label="Taxable" value={inr(totals.taxable)} />
            {form.invoice_type === "gst" && (form.is_interstate ? (
              <SumRow label="IGST" value={inr(totals.igst)} muted />
            ) : (
              <>
                <SumRow label="CGST" value={inr(totals.cgst)} muted />
                <SumRow label="SGST" value={inr(totals.sgst)} muted />
              </>
            ))}
            {totals.round_off ? <SumRow label="Round off" value={inr(totals.round_off)} muted /> : null}
            <div className="my-2 border-t border-[#F1FAEE]" />
            <SumRow label="Invoice total" value={inr(totals.invoice_total)} big />
            <div className="my-2" />
            <Field label="Advance received">
              <input type="number" min={0} value={form.advance_amount}
                     onChange={(e) => setF({ advance_amount: Number(e.target.value) || 0 })}
                     data-testid="inv-advance"
                     className="h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
            </Field>
            <SumRow label="Amount payable" value={inr(totals.amount_payable)} big />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------- helpers + subcomponents ----------

function defaultForm() {
  return {
    invoice_type: "gst",
    is_interstate: false,
    tax_inclusive: false,
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    place_of_supply: "",
    customer_id: null,
    customer_snapshot: { name: "", phone: "", email: "", gstin: "", address: "", city: "", state: "", pin: "" },
    vehicle_id: null,
    vehicle_snapshot: { reg_number: "", make: "", model: "", variant: "", fuel_type: "", vin: "", engine_number: "", odometer: null, service_type: "" },
    job_card_id: null, job_card_number: "",
    items: [],
    discount_amount: 0, discount_pct: 0, discount_reason: "",
    round_off: 0, advance_amount: 0,
    notes: "", terms: "",
  };
}

function shapeFromDraftPayload(payload) {
  return {
    ...defaultForm(),
    invoice_type: payload.invoice_type || "gst",
    is_interstate: payload.is_interstate || false,
    customer_id: payload.customer_id || null,
    customer_snapshot: payload.customer_snapshot || defaultForm().customer_snapshot,
    vehicle_id: payload.vehicle_id || null,
    vehicle_snapshot: payload.vehicle_snapshot || defaultForm().vehicle_snapshot,
    job_card_id: payload.job_card_id, job_card_number: payload.job_card_number,
    service_advisor_id: payload.service_advisor_id,
    service_advisor_name: payload.service_advisor_name,
    items: (payload.items || []).map((it) => ({ ...freshItem(it.kind || "part"), ...it })),
    advance_amount: payload.advance_amount || 0,
  };
}

function shapeFromInvoice(inv) {
  return {
    invoice_type: inv.invoice_type || "gst",
    is_interstate: inv.is_interstate || false,
    tax_inclusive: inv.tax_inclusive || false,
    invoice_date: inv.invoice_date || new Date().toISOString().slice(0, 10),
    due_date: inv.due_date || "",
    place_of_supply: inv.place_of_supply || "",
    customer_id: inv.customer_id,
    customer_snapshot: inv.customer_snapshot || defaultForm().customer_snapshot,
    vehicle_id: inv.vehicle_id,
    vehicle_snapshot: inv.vehicle_snapshot || defaultForm().vehicle_snapshot,
    job_card_id: inv.job_card_id, job_card_number: inv.job_card_number,
    items: (inv.items || []).map((it) => ({ ...freshItem(it.kind || "part"), ...it })),
    discount_amount: inv.discount_amount || 0,
    discount_pct: inv.discount_pct || 0,
    discount_reason: inv.discount_reason || "",
    round_off: inv.round_off || 0,
    advance_amount: inv.advance_amount || 0,
    notes: inv.notes || "", terms: inv.terms || "",
  };
}

function shapeToBody(f) {
  return {
    invoice_type: f.invoice_type, invoice_date: f.invoice_date || null, due_date: f.due_date || null,
    place_of_supply: f.place_of_supply || "",
    is_interstate: !!f.is_interstate, tax_inclusive: !!f.tax_inclusive,
    customer_id: f.customer_id, customer_snapshot: f.customer_snapshot,
    vehicle_id: f.vehicle_id, vehicle_snapshot: f.vehicle_snapshot,
    job_card_id: f.job_card_id, job_card_number: f.job_card_number || "",
    items: f.items.map((it) => ({
      kind: it.kind, name: it.name || "",
      part_number: it.part_number || "", description: it.description || "",
      qty: Number(it.qty) || 0, unit: it.unit || "nos",
      rate: Number(it.rate) || 0,
      discount_pct: Number(it.discount_pct) || 0, discount_amount: Number(it.discount_amount) || 0,
      gst_rate: f.invoice_type === "gst" ? Number(it.gst_rate) || 0 : 0,
      part_id: it.part_id, labour_task_id: it.labour_task_id,
      customer_supplied: !!it.customer_supplied, notes: it.notes || "",
    })),
    discount_amount: Number(f.discount_amount) || 0,
    discount_pct: Number(f.discount_pct) || 0,
    discount_reason: f.discount_reason || "",
    round_off: Number(f.round_off) || 0,
    advance_amount: Number(f.advance_amount) || 0,
    notes: f.notes || "", terms: f.terms || "",
  };
}

function freshItem(kind = "part") {
  return { kind, name: "", part_number: "", description: "", qty: 1, unit: "nos",
           rate: 0, discount_pct: 0, discount_amount: 0, gst_rate: 18,
           part_id: null, labour_task_id: null, customer_supplied: false, notes: "" };
}

function dateOnly(s) { return s ? String(s).slice(0, 10) : ""; }

function Card({ title, children, action, testId }) {
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid={testId}>
      <header className="px-4 py-3 border-b border-[#F1FAEE] flex items-center justify-between">
        <h3 className="text-[13px] font-bold text-[#1d3557]">{title}</h3>
        {action}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function SectionCard({ title, children, open, onToggle, testId }) {
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid={testId}>
      <button type="button" onClick={onToggle}
              className="w-full px-4 py-3 border-b border-[#F1FAEE] flex items-center justify-between">
        <h3 className="text-[13px] font-bold text-[#1d3557]">{title}</h3>
        {open ? <ChevronUp size={14} className="text-[#a8dadc]" /> : <ChevronDown size={14} className="text-[#a8dadc]" />}
      </button>
      {open && <div className="p-4">{children}</div>}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function SumRow({ label, value, muted, big }) {
  return (
    <div className={`flex items-center justify-between py-1 ${big ? "text-[15px] font-bold" : "text-[12.5px]"}`}>
      <span className={muted ? "text-[#667085]" : "text-[#1d3557]"}>{label}</span>
      <span className={`font-mono tabular-nums ${muted ? "text-[#667085]" : "text-[#1d3557]"}`}>{value}</span>
    </div>
  );
}

function PartyForm({ party, onChange, showGst }) {
  const setP = (k, v) => onChange({ ...party, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Field label="Name *">
        <input value={party.name || ""} onChange={(e) => setP("name", e.target.value)}
               data-testid="inv-cust-name"
               className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
      </Field>
      <Field label="Phone *">
        <input value={party.phone || ""} onChange={(e) => setP("phone", e.target.value)}
               data-testid="inv-cust-phone"
               className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
      </Field>
      <Field label="Email">
        <input type="email" value={party.email || ""} onChange={(e) => setP("email", e.target.value)}
               className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
      </Field>
      <Field label="Address">
        <input value={party.address || ""} onChange={(e) => setP("address", e.target.value)}
               className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
      </Field>
      <Field label="City">
        <input value={party.city || ""} onChange={(e) => setP("city", e.target.value)}
               className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
      </Field>
      <Field label="State">
        <input value={party.state || ""} onChange={(e) => setP("state", e.target.value)}
               className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
      </Field>
      {showGst && (
        <Field label="GSTIN">
          <input value={party.gstin || ""} onChange={(e) => setP("gstin", e.target.value.toUpperCase())}
                 data-testid="inv-cust-gstin"
                 className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
        </Field>
      )}
    </div>
  );
}

function VehicleForm({ veh, onChange }) {
  const setV = (k, v) => onChange({ ...veh, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Field label="Registration *">
        <input value={veh.reg_number || ""} onChange={(e) => setV("reg_number", e.target.value.toUpperCase())}
               data-testid="inv-veh-reg"
               className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
      </Field>
      <Field label="Make">
        <input value={veh.make || ""} onChange={(e) => setV("make", e.target.value)}
               className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
      </Field>
      <Field label="Model">
        <input value={veh.model || ""} onChange={(e) => setV("model", e.target.value)}
               className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
      </Field>
      <Field label="Odometer">
        <input type="number" value={veh.odometer ?? ""} onChange={(e) => setV("odometer", e.target.value === "" ? null : Number(e.target.value))}
               className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
      </Field>
      <Field label="Fuel type">
        <input value={veh.fuel_type || ""} onChange={(e) => setV("fuel_type", e.target.value)}
               className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
      </Field>
      <Field label="Service type">
        <input value={veh.service_type || ""} onChange={(e) => setV("service_type", e.target.value)}
               className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
      </Field>
    </div>
  );
}

function LineRow({ idx, item, parts, tasks, gstAllowed, onChange, onPart, onRemove }) {
  const [suggest, setSuggest] = useState(false);
  const source = item.kind === "part" ? parts
                : item.kind === "labour" ? (tasks || [])
                : [];
  const results = item.name
    ? source.filter((p) => (p.name || p.label || "").toLowerCase().includes(item.name.toLowerCase()) ||
                            (p.part_number || "").toLowerCase().includes(item.name.toLowerCase()))
        .slice(0, 6)
    : source.slice(0, 6);
  const KindIcon = item.kind === "part" ? Package : item.kind === "labour" ? Wrench : MoreHorizontal;
  const gross = (Number(item.qty) || 0) * (Number(item.rate) || 0);
  const lineTotal = Math.max(gross - (Number(item.discount_amount) || (gross * (Number(item.discount_pct) || 0) / 100)), 0)
                    * (gstAllowed ? (1 + (Number(item.gst_rate) || 0) / 100) : 1);
  return (
    <div className="rounded-lg border border-[#E4E7EC] p-3 space-y-2" data-testid={`inv-line-${idx}`}>
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded bg-[#F1FAEE] text-[#457b9d] grid place-items-center">
          <KindIcon size={11} />
        </span>
        <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">
          {item.kind}
        </span>
        <div className="flex-1" />
        <button type="button" onClick={onRemove} data-testid={`inv-line-${idx}-remove`}
                className="text-[#a8dadc] hover:text-[#e63946]">
          <Trash2 size={13} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-5 relative">
          {item.kind === "charge" ? (
            <select value={item.name}
                    onChange={(e) => onChange({ name: e.target.value })}
                    data-testid={`inv-line-${idx}-name`}
                    className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white">
              <option value="">Choose a charge…</option>
              {OTHER_CHARGE_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : (
            <>
              <input value={item.name} onFocus={() => setSuggest(true)}
                     onBlur={() => setTimeout(() => setSuggest(false), 200)}
                     onChange={(e) => onChange({ name: e.target.value, part_id: null })}
                     placeholder={item.kind === "part" ? "Part name" : "Labour task"}
                     data-testid={`inv-line-${idx}-name`}
                     className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
              {item.kind === "part" && (
                <input value={item.part_number || ""}
                       onChange={(e) => onChange({ part_number: e.target.value })}
                       placeholder="Part number (optional)"
                       data-testid={`inv-line-${idx}-part-number`}
                       className="mt-1.5 w-full h-8 px-2 rounded-md border border-[#E4E7EC] text-[12px] font-mono text-[#667085] focus:border-[#457b9d] outline-none" />
              )}
              {suggest && results.length > 0 && (
                <ul className="absolute z-10 mt-1 bg-white border border-[#E4E7EC] rounded-md shadow-lg w-full max-h-52 overflow-y-auto text-[12.5px]">
                  {results.map((p) => (
                    <li key={p.id}>
                      <button type="button"
                              onMouseDown={(e) => { e.preventDefault();
                                if (item.kind === "part") onPart(p);
                                else onChange({ name: p.name || p.label, labour_task_id: p.id, rate: p.rate || p.default_rate || 0, gst_rate: gstAllowed ? 18 : 0 });
                                setSuggest(false); }}
                              className="w-full text-left px-3 py-1.5 hover:bg-[#F1FAEE]">
                        <div className="font-semibold text-[#1d3557]">{p.name || p.label}</div>
                        {p.part_number && <div className="text-[10.5px] font-mono text-[#667085]">{p.part_number}</div>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        <div className="md:col-span-2">
          <div className="text-[9.5px] uppercase tracking-wider font-semibold text-[#667085] mb-1">Qty</div>
          <select value={Number(item.qty) >= 1 && Number(item.qty) <= 10 ? Number(item.qty) : 1}
                  onChange={(e) => onChange({ qty: Number(e.target.value) })}
                  data-testid={`inv-line-${idx}-qty`}
                  className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono text-center bg-white cursor-pointer">
            {Array.from({ length: 10 }, (_, n) => n + 1).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-3">
          <div className="text-[9.5px] uppercase tracking-wider font-semibold text-[#667085] mb-1">Price (Rs.)</div>
          <input type="number" min={0} value={item.rate}
                 onChange={(e) => onChange({ rate: Number(e.target.value) })}
                 placeholder="0.00" data-testid={`inv-line-${idx}-rate`}
                 className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono text-right" />
        </div>
        <div className="md:col-span-1">
          <div className="text-[9.5px] uppercase tracking-wider font-semibold text-[#667085] mb-1">Disc %</div>
          <input type="number" min={0} max={100} value={item.discount_pct}
                 onChange={(e) => onChange({ discount_pct: Number(e.target.value), discount_amount: 0 })}
                 placeholder="0"
                 data-testid={`inv-line-${idx}-disc`}
                 className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono text-right" />
        </div>
        {gstAllowed && (
          <div className="md:col-span-1">
            <div className="text-[9.5px] uppercase tracking-wider font-semibold text-[#667085] mb-1">GST %</div>
            <select value={item.gst_rate}
                    onChange={(e) => onChange({ gst_rate: Number(e.target.value) })}
                    data-testid={`inv-line-${idx}-gst`}
                    className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white font-mono">
              {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="flex justify-end text-[11.5px] font-mono text-[#1d3557]">
        Total: <b className="ml-2">{inr(lineTotal)}</b>
      </div>
    </div>
  );
}
